import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "@/hooks/useAuth"
import { fetchWithAuth } from "@/lib/fetchWithAuth"
import { toast } from "sonner"
import { Link2, AlertCircle, CheckCircle2, ExternalLink } from "lucide-react"

interface GoogleCalendarDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function GoogleCalendarDialog({ open, onOpenChange }: GoogleCalendarDialogProps) {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [syncEnabled, setSyncEnabled] = useState(false)
  const [oauthConnected, setOauthConnected] = useState(false)
  const [calendarId, setCalendarId] = useState("primary")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open && user) {
      void loadSettings()
    }
  }, [open, user])

  const loadSettings = async () => {
    if (!user) return

    setLoading(true)
    try {
      const res = await fetchWithAuth("/api/google-calendar/status")
      const body = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(body.error || "Erro ao carregar status")
      }
      setOauthConnected(!!body.oauthConnected)
      setSyncEnabled(!!body.syncEnabled)
      setCalendarId(body.calendarId && String(body.calendarId).trim() !== "" ? body.calendarId : "primary")
    } catch (error) {
      console.error("Erro ao carregar configurações:", error)
      toast.error("Não foi possível carregar o status do Google Calendar.")
    } finally {
      setLoading(false)
    }
  }

  const persistCalendarId = async (nextId: string) => {
    if (!user || !oauthConnected) return
    const cid = nextId.trim() === "" ? "primary" : nextId.trim()
    const { error } = await supabase
      .from("user_google_calendar_settings")
      .update({ calendar_id: cid })
      .eq("user_id", user.id)
    if (error) throw error
  }

  const handleConnect = async () => {
    if (!user) return

    setSaving(true)
    try {
      const cid = calendarId.trim() === "" ? "primary" : calendarId.trim()
      const res = await fetchWithAuth(
        `/api/google-calendar/oauth-url?calendar_id=${encodeURIComponent(cid)}`
      )
      const body = await res.json().catch(() => ({}))
      if (!res.ok) {
        toast.error(body.error || "Não foi possível iniciar a conexão com o Google.")
        if (body.hint) {
          console.warn(body.hint)
        }
        return
      }
      if (!body.url || typeof body.url !== "string") {
        toast.error("Resposta inválida do servidor.")
        return
      }
      window.location.href = body.url
    } catch (error) {
      console.error("Erro ao conectar:", error)
      toast.error("Erro ao conectar com Google Calendar")
    } finally {
      setSaving(false)
    }
  }

  const handleDisconnect = async () => {
    if (!user) return

    setSaving(true)
    try {
      const res = await fetchWithAuth("/api/google-calendar/disconnect", { method: "POST" })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.error || "disconnect failed")

      setOauthConnected(false)
      setSyncEnabled(false)
      setCalendarId("primary")
      toast.success("Google Calendar desconectado")
    } catch (error) {
      console.error("Erro ao desconectar:", error)
      toast.error("Erro ao desconectar Google Calendar")
    } finally {
      setSaving(false)
    }
  }

  const handleToggleSync = async (enabled: boolean) => {
    if (!user || !oauthConnected) return

    setSaving(true)
    try {
      const { error } = await supabase
        .from("user_google_calendar_settings")
        .update({ sync_enabled: enabled })
        .eq("user_id", user.id)

      if (error) throw error

      setSyncEnabled(enabled)
      toast.success(enabled ? "Sincronização ativada" : "Sincronização desativada")
    } catch (error) {
      console.error("Erro ao atualizar sincronização:", error)
      toast.error("Erro ao atualizar configuração")
    } finally {
      setSaving(false)
    }
  }

  const handleBlurCalendarId = async () => {
    if (!oauthConnected || saving || loading) return
    try {
      await persistCalendarId(calendarId)
    } catch (e) {
      console.error(e)
      toast.error("Não foi possível salvar o ID do calendário")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Integração com Google Calendar
          </DialogTitle>
          <DialogDescription>
            Conecte sua conta Google para importar eventos e enviar novos compromissos ao seu calendário.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {oauthConnected ? (
            <Alert className="border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-900">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertDescription className="text-green-800 dark:text-green-200">
                Conta Google autorizada. Use &quot;Sincronizar&quot; na página do calendário para importar eventos.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Você será redirecionado ao Google para autorizar o acesso ao Calendar (escopo de eventos).
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="calendarId">ID do calendário</Label>
            <Input
              id="calendarId"
              placeholder="primary"
              value={calendarId}
              onChange={(e) => setCalendarId(e.target.value)}
              onBlur={() => void handleBlurCalendarId()}
              disabled={saving || loading}
            />
            <p className="text-xs text-muted-foreground">
              Use <code className="text-xs">primary</code> para o calendário principal ou o ID de um calendário
              compartilhado (ex.: <code className="text-xs">xxxx@group.calendar.google.com</code>).
            </p>
          </div>

          {oauthConnected && (
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="space-y-0.5">
                <Label htmlFor="sync" className="text-base">
                  Sincronização automática
                </Label>
                <p className="text-sm text-muted-foreground">
                  Permite importar do Google e replicar eventos criados aqui no Google Calendar
                </p>
              </div>
              <Switch
                id="sync"
                checked={syncEnabled}
                onCheckedChange={handleToggleSync}
                disabled={saving}
              />
            </div>
          )}

          <div className="p-4 border border-dashed rounded-lg">
            <h4 className="font-semibold text-sm mb-2">Configuração no Google Cloud</h4>
            <p className="text-xs text-muted-foreground mb-2">
              Crie credenciais OAuth 2.0 (aplicação Web), ative a Google Calendar API e defina a URI de redirecionamento
              exatamente igual à variável <code className="text-xs">GOOGLE_CALENDAR_REDIRECT_URI</code> no backend
              (por exemplo <code className="text-xs">http://localhost:3001/api/google-calendar/callback</code>).
            </p>
            <div className="space-y-2">
              <a
                href="https://console.cloud.google.com/apis/credentials"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                Google Cloud Console — Credenciais
              </a>
              <a
                href="https://developers.google.com/calendar/api/guides/auth"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                Autenticação — Calendar API
              </a>
            </div>
          </div>
        </div>

        <DialogFooter>
          {oauthConnected ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => void handleDisconnect()}
                disabled={saving}
              >
                Desconectar
              </Button>
              <Button type="button" onClick={() => onOpenChange(false)}>
                Fechar
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => void handleConnect()}
                disabled={saving || loading}
                className="gap-2"
              >
                <Link2 className="h-4 w-4" />
                {saving ? "Redirecionando..." : "Conectar com Google"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
